import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, Reply, Send, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const fmt = (ts) => new Date(ts).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" });

function Composer({ lead, mailboxes, thread, onSent, onCancel }) {
  const last = thread?.messages?.[thread.messages.length - 1];
  const [from, setFrom] = useState(thread?.mailbox || mailboxes[0] || "");
  const [subject, setSubject] = useState(thread ? (/^re:/i.test(thread.subject) ? thread.subject : `Re: ${thread.subject}`) : "");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and message are required"); return; }
    setBusy(true);
    try {
      await api.post(`/crm/leads/${lead._id}/emails/send`, { from_mailbox: from, subject, body, thread_id: thread?.id, in_reply_to: last?.message_id });
      toast.success("Email sent"); onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel-2 p-3 space-y-2" data-testid="email-composer">
      <div className="flex flex-wrap gap-2 text-xs items-center">
        <span className="text-neutral-500">From</span>
        <select className="px-2 py-1 rounded border text-xs" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="email-from">
          {mailboxes.map((m) => <option key={m}>{m}</option>)}
        </select>
        <span className="text-neutral-500">To</span><span className="font-mono">{lead.email}</span>
      </div>
      <input className="w-full px-2.5 py-1.5 rounded border text-sm" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" />
      <textarea rows={6} className="w-full px-2.5 py-1.5 rounded border text-sm" placeholder="Write your message…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body" />
      <div className="flex justify-end gap-2">
        <button className="btn-ghost btn text-xs" onClick={onCancel} data-testid="email-cancel">Cancel</button>
        <button className="btn btn-primary text-xs" onClick={send} disabled={busy} data-testid="email-send"><Send size={12} />{busy ? "Sending…" : "Send"}</button>
      </div>
    </div>
  );
}

function Thread({ t, onReply }) {
  const [open, setOpen] = useState(false);
  const last = t.messages[t.messages.length - 1];
  return (
    <div className="border border-[var(--border)] rounded" data-testid={`email-thread-${t.id}`}>
      <button className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-[var(--panel-2)]" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown size={14} className="mt-0.5" /> : <ChevronRight size={14} className="mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2"><span className="text-sm font-medium break-words">{t.subject}</span><span className="text-[10px] text-neutral-500">{t.count}</span>
            <span className="ml-auto text-[11px] text-neutral-500 whitespace-nowrap">{fmt(t.last_ts)}</span></div>
          <div className="text-xs text-neutral-500 break-words">{last.snippet}</div>
          <div className="text-[10px] text-neutral-600">via {t.mailbox}</div>
        </div>
      </button>
      {open && (
        <div className="border-t border-[var(--border)] divide-y divide-[var(--border)]">
          {t.messages.map((m) => (
            <div key={m.id} className="px-3 py-2.5">
              <div className="flex text-xs gap-2"><span className="text-neutral-300 break-all">{m.from}</span><span className="ml-auto text-neutral-500 whitespace-nowrap">{fmt(m.internal_ts)}</span></div>
              <div className="text-[11px] text-neutral-600 break-all">to {m.to}</div>
              <div className="text-sm text-neutral-300 whitespace-pre-wrap mt-2 break-words max-h-80 overflow-auto">{m.body || m.snippet}</div>
            </div>
          ))}
          <div className="px-3 py-2"><button className="btn text-xs" onClick={() => onReply(t)} data-testid={`email-reply-${t.id}`}><Reply size={12} />Reply</button></div>
        </div>
      )}
    </div>
  );
}

export default function LeadEmails({ lead, onSent }) {
  const [d, setD] = useState(null);
  const [compose, setCompose] = useState(null);
  const load = () => api.get(`/crm/leads/${lead._id}/emails`).then((r) => setD(r.data)).catch(() => setD({ threads: [], mailboxes: [], error: true }));
  useEffect(() => { setD(null); load(); }, [lead._id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="text-xs text-neutral-500">Loading Gmail threads…</div>;
  if (!lead.email) return <div className="text-xs text-neutral-500">This lead has no email address.</div>;
  if (!d.mailboxes.length) return <div className="text-xs text-neutral-400" data-testid="emails-not-connected">No mailbox has granted Gmail access yet. Go to Settings → Google and click <b>Reconnect</b> on khush@ / vedant@ / prakhar@ to show and send emails here.</div>;
  return (
    <div className="space-y-2" data-testid="lead-emails">
      {compose ? <Composer lead={lead} mailboxes={d.mailboxes} thread={compose === "new" ? null : compose} onCancel={() => setCompose(null)} onSent={() => { setCompose(null); load(); onSent?.(); }} />
        : <button className="btn btn-primary text-xs" onClick={() => setCompose("new")} data-testid="email-new-btn"><Mail size={12} />New email</button>}
      {!d.threads.length ? <div className="text-xs text-neutral-600 py-3">No emails with {lead.email} in {d.mailboxes.join(", ")}.</div>
        : d.threads.map((t) => <Thread key={`${t.mailbox}-${t.id}`} t={t} onReply={setCompose} />)}
    </div>
  );
}
